import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

// In-page modal that fetches and renders /policy/terms or /policy/privacy
// (the same content surfaced at /terms and /privacy) without navigating away
// from the SSO claim interstitial. We hit the .json variant because the HTML
// view uses react_component(prerender: false) and contains no readable text
// in the rendered HTML. Body HTML is trusted: SimpleContent is admin-managed
// and the same body is already rendered via dangerouslySetInnerHTML in the
// existing Policy.js component.

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(24px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
  animation: ${fadeIn} 0.15s ease-out;

  @media (max-width: 640px) {
    padding: 0;
    align-items: flex-end;
  }
`;

const Sheet = styled.div`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radiusLg};
  width: 100%;
  max-width: 560px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.2s ease-out;

  @media (max-width: 640px) {
    max-width: 100%;
    max-height: 90vh;
    border-radius: ${p => p.theme.radiusLg} ${p => p.theme.radiusLg} 0 0;
    border-bottom: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${p => p.theme.border};
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${p => p.theme.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.textSecondary};
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: ${p => p.theme.radius};

  &:hover { color: ${p => p.theme.text}; background: ${p => p.theme.surface2}; }
`;

const Body = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  color: ${p => p.theme.textSecondary};
  font-size: 14px;
  line-height: 22px;

  h1, h2, h3, h4 { color: ${p => p.theme.text}; margin-top: 16px; margin-bottom: 8px; }
  h1 { font-size: 20px; }
  h2 { font-size: 17px; }
  h3 { font-size: 15px; }
  p  { margin-bottom: 12px; }
  ul, ol { margin: 8px 0 12px 20px; }
  li { margin-bottom: 6px; }
  a  { color: ${p => p.theme.accent}; }
  strong { color: ${p => p.theme.text}; }
`;

const Loading = styled.div`
  text-align: center;
  padding: 24px;
  color: ${p => p.theme.textTertiary};
  font-size: 14px;
`;

const ErrorText = styled.div`
  text-align: center;
  padding: 24px;
  color: ${p => p.theme.error};
  font-size: 14px;
`;

const FALLBACK_TITLES = {
  terms:   'Terms of Service',
  privacy: 'Privacy Policy',
};

export default function PolicyModal({ kind, onClose }) {
  const [title, setTitle]     = useState(FALLBACK_TITLES[kind]);
  const [body, setBody]       = useState(null);
  const [error, setError]     = useState(null);
  const closeRef              = useRef(null);
  const previouslyFocused     = useRef(null);

  useEffect(function() {
    let cancelled = false;
    setTitle(FALLBACK_TITLES[kind]);
    setBody(null);
    setError(null);

    fetch('/policy/' + kind + '.json', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    })
      .then(function(res) {
        if (!res.ok) throw new Error('Could not load ' + FALLBACK_TITLES[kind]);
        return res.json();
      })
      .then(function(data) {
        if (cancelled) return;
        if (data && typeof data.title === 'string') setTitle(data.title);
        setBody(typeof data.body === 'string' ? data.body : '');
      })
      .catch(function(err) {
        if (cancelled) return;
        setError(err.message);
      });

    return function() { cancelled = true; };
  }, [kind]);

  useEffect(function() {
    previouslyFocused.current = document.activeElement;
    if (closeRef.current) closeRef.current.focus();

    const onKey = function(e) {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return function() {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused.current && previouslyFocused.current.focus) {
        previouslyFocused.current.focus();
      }
    };
  }, [onClose]);

  const onOverlayClick = function(e) {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <Overlay role="dialog" aria-modal="true" aria-label={title} onClick={onOverlayClick}>
      <Sheet>
        <Header>
          <Title>{title}</Title>
          <CloseButton ref={closeRef} type="button" onClick={onClose} aria-label="Close">×</CloseButton>
        </Header>
        {error
          ? <ErrorText>{error}</ErrorText>
          : body == null
            ? <Loading>Loading…</Loading>
            : <Body dangerouslySetInnerHTML={{ __html: body }} />}
      </Sheet>
    </Overlay>
  );
}
